/**
 * Channel detail → "Today's Performance" grid, sourced from the Owners Hub Stats API.
 *
 * Reads the channel's slice of `daily_affiliate_activity` filtered to its affiliate
 * type (`aft`), the same per-`aft` source the Home Acquisition Channels cards use.
 * The grid is a live Today snapshot (current UTC day) with a "vs yesterday" delta from
 * a SEPARATE prior-day call (the API has no comparison field); it re-polls ~every 5
 * minutes. Registrations / FTDs / Avg FTD Value are sourced; Daily Spend has no stats
 * field → `?` (see {@link CHANNEL_METRIC_SPECS}). Like the Home channel cards, these
 * read live but show zeros until the backend tags affiliate rows with a non-empty `aft`.
 */
import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchStatsDataSetHistory,
  selectStatsDataSetHistory,
  selectStatsDataSetHistoryError,
  selectStatsDataSetHistoryLoading,
  type StatsHistoryRef,
} from '@/store/slices/ownerHubStats';

import type { ChannelId, ChannelMetric } from '../../mock-data/types';
import { homeWindow, shiftWindow } from '../home/stats-mapping';
import { liveBuckets, matchedBaseline, toBucketsFor } from '../home/stats-shape';
import { LIVE_REFRESH_MS, useRefreshTick } from '../home/useRefreshTick';
import { buildChannelCpa, buildChannelMetrics, CHANNEL_AFT, type ChannelAdSpend } from './channel-performance';

const DATA_SET = 'daily_affiliate_activity';
const AGGREGATE_ON = ['aft'];
const DIMENSION = 'aft';

/** The channel ad-spend figures the grid + CPA card need, sourced from
 *  {@link useChannelCampaigns}: today / yesterday cost, plus its loading / error state. */
export type ChannelAdCost = {
  costToday: number | null;
  costYesterday: number | null;
  errored: boolean;
  loading: boolean;
};

export type ChannelPerformanceResult = { metrics: ChannelMetric[]; cpa: ChannelMetric; loading: boolean };

/**
 * @param channelId The channel page id (drives the `aft` filter); `undefined` keeps
 *        the grid loading.
 * @param casinoInstanceId Active brand id (`displayBrand?.id`); `undefined` keeps the
 *        grid loading until a brand resolves.
 * @param scaffold The "Today's Performance" metric scaffold (static id / label); the
 *        figures are overridden with the API-sourced ones.
 * @param cpaScaffold The top CPA card scaffold (id / label); its figure is built from
 *        the channel ad spend ÷ FTDs.
 * @param adCost The channel ad-spend (today / yesterday cost + loading / error state),
 *        behind the Daily Spend tile and the CPA card.
 */
export const useChannelPerformance = (
  channelId: ChannelId | undefined,
  casinoInstanceId: string | undefined,
  scaffold: ChannelMetric[],
  cpaScaffold: ChannelMetric,
  adCost: ChannelAdCost,
): ChannelPerformanceResult => {
  const dispatch = useAppDispatch();
  const aft = channelId ? CHANNEL_AFT[channelId] : undefined;

  // Today re-aggregates ~every 5 min; the tick re-polls in place and rolls the window
  // over at UTC midnight (the clock read is a hidden time dep).
  const tick = useRefreshTick(true, LIVE_REFRESH_MS);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const window = React.useMemo(() => homeWindow('TODAY', Date.now()), [tick]);
  const priorWindow = React.useMemo(() => shiftWindow(window, window.spanDays), [window]);

  const ready = Boolean(casinoInstanceId && aft);
  const mainRef: StatsHistoryRef | undefined = ready
    ? {
        casinoInstanceId: casinoInstanceId as string,
        dataSet: DATA_SET,
        startDate: window.startDate,
        endDate: window.endDate,
        intervalSlice: window.intervalSlice,
        aggregateOn: AGGREGATE_ON,
        filter: { aft: aft as string },
      }
    : undefined;
  const priorRef: StatsHistoryRef | undefined = ready
    ? {
        casinoInstanceId: casinoInstanceId as string,
        dataSet: DATA_SET,
        startDate: priorWindow.startDate,
        endDate: priorWindow.endDate,
        intervalSlice: window.intervalSlice,
        aggregateOn: AGGREGATE_ON,
        filter: { aft: aft as string },
      }
    : undefined;

  const mainEntry = useAppSelector((s) => selectStatsDataSetHistory(s, mainRef));
  const mainLoading = useAppSelector((s) => selectStatsDataSetHistoryLoading(s, mainRef));
  const mainError = useAppSelector((s) => selectStatsDataSetHistoryError(s, mainRef));
  const priorEntry = useAppSelector((s) => selectStatsDataSetHistory(s, priorRef));

  // Current-day call, re-dispatched on the tick so Today refreshes in place.
  React.useEffect(() => {
    if (!ready) return;
    dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId: casinoInstanceId as string,
        dataSet: DATA_SET,
        startDate: window.startDate,
        endDate: window.endDate,
        intervalSlice: window.intervalSlice,
        aggregateOn: [...AGGREGATE_ON],
        filter: { aft: aft as string },
      }),
    );
  }, [dispatch, ready, casinoInstanceId, aft, window.startDate, window.endDate, window.intervalSlice, tick]);

  // Separate prior-day call for the delta baseline.
  React.useEffect(() => {
    if (!ready) return;
    dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId: casinoInstanceId as string,
        dataSet: DATA_SET,
        startDate: priorWindow.startDate,
        endDate: priorWindow.endDate,
        intervalSlice: window.intervalSlice,
        aggregateOn: [...AGGREGATE_ON],
        filter: { aft: aft as string },
      }),
    );
  }, [dispatch, ready, casinoInstanceId, aft, priorWindow.startDate, priorWindow.endDate, window.intervalSlice]);

  // Read the group matching this channel's `aft` from its filtered response (robust
  // whether the server narrows to the one group or echoes several). Drop future buckets.
  const current = mainEntry && aft ? liveBuckets(toBucketsFor(mainEntry.data, DIMENSION, aft), Date.now()) : [];
  const prior = priorEntry && aft ? toBucketsFor(priorEntry.data, DIMENSION, aft) : [];
  const baseline = matchedBaseline(current, prior);

  // Stale-while-revalidate: skeleton only when there is no cached entry yet.
  const statsLoading = !ready || (mainEntry === undefined && (mainLoading || mainError === undefined));
  const errored = !statsLoading && mainError !== undefined && mainEntry === undefined;

  // The cost-backed figures (Daily Spend tile + CPA card) read the ad spend plus the
  // channel's own `aft` FTD buckets (CPA = cost / FTDs).
  const adSpend: ChannelAdSpend = {
    cost: adCost.costToday,
    baselineCost: adCost.costYesterday,
    current,
    baseline,
    errored: adCost.errored,
    loading: adCost.loading,
  };

  return {
    metrics: buildChannelMetrics(scaffold, current, baseline, errored, adSpend),
    cpa: buildChannelCpa(cpaScaffold, adSpend),
    // The grid + CPA reveal together once both the stats and the ad spend resolve.
    loading: statsLoading || adCost.loading,
  };
};

export default useChannelPerformance;
